import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  BarChart3,
  Settings,
  DollarSign,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Shield,
  Sun,
  Moon,
  Monitor,
  Gauge,
  Megaphone,
  BookOpen,
  HelpCircle,
  Search,
  Receipt,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore, type Theme } from '../../store/themeStore';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../Common/LanguageSwitcher';
import { NotificationBell } from '../Common/NotificationBell';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { user, logout } = useAuthStore();
  const { theme, resolved, setTheme } = useThemeStore();
  // Desktop collapse state (w-20 / w-64). On mobile (<lg) the sidebar
  // is fully off-canvas and `mobileSidebarOpen` controls visibility.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  );

  // Auto-close drawer on route change.
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (mobileSidebarOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
    return undefined;
  }, [mobileSidebarOpen]);

  // Watch lg breakpoint so we can collapse-only on desktop.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsDesktop('matches' in e ? e.matches : (e as MediaQueryList).matches);
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // True only on desktop AND when explicitly collapsed — mobile
  // drawer always shows the full expanded layout.
  const effectiveCollapsed = isDesktop && sidebarCollapsed;

  // Same theme model as UserLayout — single global store flips the
  // `dark` class on <html>, so the toggle here affects the user
  // dashboard too. Operators expect their theme choice to persist
  // when crossing the admin/user boundary; using the shared store
  // makes that automatic.
  const themeOptions: { id: Theme; icon: React.ReactNode; label: string }[] = [
    { id: 'light', icon: <Sun className="w-4 h-4" />, label: t('theme.light') },
    { id: 'system', icon: <Monitor className="w-4 h-4" />, label: t('theme.auto') },
    { id: 'dark', icon: <Moon className="w-4 h-4" />, label: t('theme.dark') },
  ];

  // Collapsed-sidebar shortcut: cycle light → system → dark → light.
  // Mirrors UserLayout so muscle memory carries over.
  const cycleTheme = () => {
    const order: Theme[] = ['light', 'system', 'dark'];
    const next = order[(order.indexOf(theme) + 1) % order.length];
    setTheme(next);
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: t('nav.dashboard'),
      icon: <LayoutDashboard className="w-5 h-5" />,
      path: '/admin',
    },
    {
      id: 'users',
      label: t('nav.userManagement'),
      icon: <Users className="w-5 h-5" />,
      path: '/admin/users',
    },
    {
      id: 'pages',
      label: t('nav.landingPages'),
      icon: <FileText className="w-5 h-5" />,
      path: '/admin/pages',
    },
    {
      id: 'analytics',
      label: t('nav.analytics'),
      icon: <BarChart3 className="w-5 h-5" />,
      path: '/admin/analytics',
    },
    {
      id: 'cloaking',
      label: t('nav.botCloaking'),
      icon: <Shield className="w-5 h-5" />,
      path: '/admin/cloaking',
    },
    {
      id: 'usage',
      label: t('nav.usage'),
      icon: <Gauge className="w-5 h-5" />,
      path: '/admin/usage',
    },
    {
      id: 'broadcasts',
      label: t('nav.broadcasts'),
      icon: <Megaphone className="w-5 h-5" />,
      path: '/admin/broadcasts',
    },
    // Content & SEO — grouped because they share the platform's
    // public marketing surface. Articles + FAQs are the primary
    // content production tools; SEO settings drives JSON-LD, robots,
    // sitemap, and llms.txt across every SSR'd page.
    {
      id: 'articles',
      label: t('nav.articles', { defaultValue: 'Articles' }),
      icon: <BookOpen className="w-5 h-5" />,
      path: '/admin/articles',
    },
    {
      id: 'faqs',
      label: t('nav.faqs', { defaultValue: 'FAQs' }),
      icon: <HelpCircle className="w-5 h-5" />,
      path: '/admin/faqs',
    },
    {
      id: 'seo',
      label: t('nav.seoSettings', { defaultValue: 'SEO Settings' }),
      icon: <Search className="w-5 h-5" />,
      path: '/admin/seo',
    },
    {
      id: 'financial',
      label: t('nav.financialReports'),
      icon: <DollarSign className="w-5 h-5" />,
      path: '/admin/financial',
    },
    {
      id: 'invoices',
      label: t('nav.manualInvoices', { defaultValue: 'Manual Invoices' }),
      icon: <Receipt className="w-5 h-5" />,
      path: '/admin/invoices',
    },
    {
      id: 'settings',
      label: t('nav.systemSettings'),
      icon: <Settings className="w-5 h-5" />,
      path: '/admin/settings',
    },
  ];

  const handleLogout = () => {
    logout();
    toast.success(t('auth.loggedOut'));
    navigate('/login');
  };

  const isActivePath = (path: string) => {
    // Dashboard (`/admin`) must match exactly so it doesn't light up
    // for every other admin sub-route. All other items match on a
    // prefix so deep links (e.g. `/admin/cloaking/:pageId`) still
    // highlight their parent menu item.
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <div className="flex h-screen bg-gray-50 flex-col">
      {/* Mobile top bar — hamburger + brand. Hidden on lg+ where the
          in-flow sidebar carries its own header. */}
      <div className="lg:hidden flex items-center justify-between gap-3 h-14 px-3 sm:px-4 bg-white border-b border-gray-200 flex-shrink-0">
        <button
          type="button"
          onClick={() => setMobileSidebarOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-700"
          aria-label={t('actions.openMenu', { defaultValue: 'Open menu' })}
        >
          <Menu className="w-6 h-6" />
        </button>

        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 min-w-0"
        >
          <div className="w-7 h-7 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center shrink-0">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-gray-900 text-sm truncate">
            {t('brand.adminPanel')}
          </span>
        </button>

        <div className="shrink-0">
          <NotificationBell variant="sidebar-collapsed" />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative">
      {/* Mobile drawer backdrop */}
      {mobileSidebarOpen && (
        <button
          type="button"
          aria-label={t('actions.closeMenu', { defaultValue: 'Close menu' })}
          onClick={() => setMobileSidebarOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        />
      )}

      {/* Sidebar — desktop in-flow, mobile off-canvas overlay. */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 transform bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out
          ${mobileSidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:shadow-none lg:z-auto lg:w-auto
          ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
        `}
      >
        {/* Logo/Brand */}
        <div className="h-16 border-b border-gray-200 flex items-center justify-between px-4 shrink-0">
          {/* Mobile: full brand */}
          <div className="flex items-center gap-2 lg:hidden">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-gray-900">{t('brand.adminPanel')}</span>
          </div>

          {/* Desktop: respects collapse state */}
          {!sidebarCollapsed && (
            <div className="hidden lg:flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-gray-900">{t('brand.adminPanel')}</span>
            </div>
          )}

          {/* Mobile close (X) */}
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
            aria-label={t('actions.closeMenu', { defaultValue: 'Close menu' })}
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>

          {/* Desktop collapse */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors hidden lg:flex"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <Menu className="w-5 h-5 text-gray-600" />
            ) : (
              <X className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>

        {/* Notification bell — admins also act on lifecycle/billing
            events, so they get the same prominent sidebar slot
            instead of the old (and removed) top header bar. */}
        <div className="px-3 py-2 border-b border-gray-200">
          <NotificationBell
            variant={effectiveCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {menuItems.map((item) => {
              const isActive = isActivePath(item.path);
              return (
                <li key={item.id}>
                  <button
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    title={effectiveCollapsed ? item.label : ''}
                  >
                    <span className={isActive ? 'text-blue-600' : 'text-gray-500'}>
                      {item.icon}
                    </span>
                    {!effectiveCollapsed && (
                      <>
                        <span className="flex-1 text-left text-sm">{item.label}</span>
                        {isActive && <ChevronRight className="w-4 h-4" />}
                      </>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Switch to User Dashboard — mirror of the "Admin Panel"
              link in UserLayout. Lets the operator pop back into a
              normal-user view to verify what their customers see
              (or just to use the platform as a paying user would).
              Uses a blue/purple gradient to contrast with the
              yellow/orange admin badge in UserLayout, so the colour
              cue tells you which "side" you're switching to. */}
          {!effectiveCollapsed ? (
            <div className="px-3 mt-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full flex items-center gap-3 px-3 py-2.5 bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 rounded-lg transition-all border border-blue-200"
              >
                <LayoutDashboard className="w-5 h-5 text-blue-600" />
                <div className="flex-1 text-left">
                  <span className="text-sm font-semibold text-blue-900">{t('nav.userDashboard')}</span>
                  <p className="text-xs text-blue-700">{t('nav.userDashboardDescription')}</p>
                </div>
              </button>
            </div>
          ) : (
            <div className="px-3 mt-4 flex justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 rounded-lg transition-all border border-blue-200"
                title={t('nav.userDashboard')}
              >
                <LayoutDashboard className="w-5 h-5 text-blue-600" />
              </button>
            </div>
          )}
        </nav>

        {/* User Profile + Language + Theme + Logout */}
        <div className="border-t border-gray-200 p-4">
          {!effectiveCollapsed ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 px-3 py-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {user?.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
              <div className="flex justify-center">
                <LanguageSwitcher size="sm" variant="light" className="w-full justify-center" />
              </div>

              {/* Theme segmented control — mirrors UserLayout. The label
                  is hidden below xl breakpoint to keep the segments
                  comfortably tappable in the narrower admin sidebar. */}
              <div
                className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1"
                role="group"
                aria-label={t('theme.label')}
              >
                {themeOptions.map((opt) => {
                  const isActive = theme === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setTheme(opt.id)}
                      className={`flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title={opt.label}
                      aria-pressed={isActive}
                    >
                      {opt.icon}
                      <span className="hidden xl:inline">{opt.label}</span>
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
              >
                <LogOut className="w-5 h-5" />
                <span>{t('actions.logout')}</span>
              </button>
              <p className="text-[10px] text-gray-400 text-center leading-tight">
                {t('brand.copyright', { year: new Date().getFullYear() })}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                {user?.name.charAt(0).toUpperCase()}
              </div>
              <button
                type="button"
                onClick={cycleTheme}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('theme.current', { theme, resolved })}
              >
                {resolved === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              <button
                onClick={handleLogout}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title={t('actions.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
      </div>
    </div>
  );
};

